import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { byggNotisLank } from './notificationRoutes';

// Routingen ligger i notificationRoutes.js — getWorkspaceSubTab som låg här
// mappade bara en handfull typer och lät resten falla till kanban.

function NotificationIcon() {
  // Komponenten är nu helt självständig utan props
  const [notifications, setNotifications] = useState([]);
  const [visibleNotifications, setVisibleNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [projectLabelMap, setProjectLabelMap] = useState({});
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const socket = useSocket();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Debug: Logga när komponenten mountas
  useEffect(() => {
    return () => {
    };
  }, []);
  
  // 🚀 OPTIMERING: Lägg till ref för att undvika dubbla anrop
  const lastFetchTime = useRef(0);
  const FETCH_THROTTLE_MS = 1000; // Begränsa till ett anrop per sekund

  const fetchNotifications = useCallback(async () => {
    try {
      // 🚀 OPTIMERING: Throttling för att undvika dubbla anrop
      const now = Date.now();
      if (now - lastFetchTime.current < FETCH_THROTTLE_MS) {
        return;
      }
      lastFetchTime.current = now;
      
      const token = localStorage.getItem('token');
      
      // Om token saknas, skippa anropet
      if (!token) {
        return;
      }
      
      const response = await fetch(`${API_URL}/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401 || response.status === 400) {
        // Token är ogiltig, användaren behöver logga in igen
        localStorage.removeItem('token');
        return;
      }

      if (!response.ok) {
        throw new Error('Kunde inte hämta notifieringar');
      }

      const data = await response.json();
      
      // Filtrera bort egna åtgärder, MEN behåll PU-notiser även vid självtest
      const filteredData = data.filter(notification => {
        const isSelf = String(notification.actionUserId) === String(user?._id);
        const isPU = String(notification.type || '').startsWith('pu');
        // Byggare ska aldrig se sina egna PU-notiser
        if (user?.role === 'byggare' && isSelf && isPU) return false;
        // Övriga: filtrera bort egna åtgärder utom när vi uttryckligen vill tillåta PU för självtest
        return !(isSelf && !isPU);
      });
      
      setNotifications(filteredData);
      // Prefetch projektlabels där de saknas
      try {
        await prefetchProjectLabels(filteredData);
      } catch (_) {}
      const unreadNotifications = filteredData.filter(n => !n.read).length;
      setUnreadCount(unreadNotifications);
    } catch (error) {
      // Mer förlåtande felhantering - logga felet men låt komponenten fungera
      console.error('Fel vid hämtning av notifieringar:', error);
      // Håll kvar befintliga notifikationer istället för att nollställa
    }
    // prefetchProjectLabels utelämnad med avsikt: den definieras längre ned i
    // komponenten (rad ~149), och att referera den här läses under renderingen
    // innan const tilldelats — alltså ReferenceError. Den är useCallback och
    // byts bara när user?.role ändras, som nu står i listan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.role]);

  // Hämta projektnamn från lead när metadata.projectId saknas
  const fetchProjectLabelForNotification = useCallback(async (notification) => {
    try {
      // Endast admin och byggkonsult har behörighet att hämta lead-data
      const allowedRoles = ['admin', 'byggkonsult'];
      if (!allowedRoles.includes(user?.role)) {
        return null;
      }

      const meta = notification?.metadata || {};
      let leadId = meta.projectId;
      // Validera leadId; om ogiltigt, försök hämta via PU meta
      const validObjectId = (id) => !!id && /^[a-fA-F0-9]{24}$/.test(String(id));
      if (!validObjectId(leadId) && validObjectId(meta.puId)) {
        try {
          const token = localStorage.getItem('token');
          const mres = await fetch(`${API_URL}/api/pu/${meta.puId}/meta`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (mres.ok) {
            const m = await mres.json();
            leadId = m?.meta?.leadId || m?.leadId || leadId;
          }
        } catch (_) {}
      }
      if (!validObjectId(leadId)) return null;

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/leads/${leadId}`, { headers: { 'Authorization': `Bearer ${token}` } });

      // Hantera 404-fel och 403-fel tyst
      if (!res.ok) {
        if (res.status === 404) {
          // 404 är normalt - lead kan ha tagits bort men notifikationen finns kvar
          console.debug(`🔍 Lead ${leadId} existerar inte längre (404) - hoppar över projektlabel`);
        } else if (res.status === 403) {
          // 403 är normalt för roller utan behörighet (t.ex. inredningsarkitekt)
          console.debug(`🔍 Ingen behörighet att hämta lead ${leadId} (403) - hoppar över projektlabel`);
        } else {
          // Logga andra fel för debugging
          console.warn(`⚠️ Kunde inte hämta lead ${leadId}:`, res.status, res.statusText);
        }
        return null;
      }

      const data = await res.json();
      const leadObj = data && data._id ? data : data?.lead;
      const name = leadObj?.customerInfo?.name || '';
      const address = leadObj?.projectInfo?.address || '';
      const pid = leadObj?.projectId || '';
      const parts = [name, address, pid ? `#${pid}` : ''].filter(Boolean);
      return parts.length > 0 ? parts.join(' - ') : null;
    } catch (error) {
      // Logga oväntat fel
      console.debug('🔍 Fel vid hämtning av projektlabel för notifikation:', error.message);
      return null;
    }
  }, [user?.role]);

  const prefetchProjectLabels = useCallback(async (notifs) => {
    const entries = await Promise.all((notifs || []).map(async n => {
      const meta = n.metadata || {};
      if (meta.projectId) return [n._id, `#${meta.projectId}`];
      const label = await fetchProjectLabelForNotification(n);
      return [n._id, label];
    }));
    setProjectLabelMap(prev => {
      const next = { ...prev };
      for (const [id, label] of entries) {
        if (label) next[id] = label;
      }
      return next;
    });
  }, [fetchProjectLabelForNotification]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (socket) {
      socket.on('notification', (data) => {
        // Hantera standardiserade notiser med 'notification' objekt
        if (data?.notification && data.notification._id) {
          // Se till att användaren finns innan vi jämför ID
          if (!user || !user._id) {
            return;
          }
          
          // Filtrera bort egna åtgärder, MEN behåll PU-notiser även vid självtest
          const isSelf = String(data.notification.actionUserId) === String(user._id);
          const isPU = String(data.notification.type || '').startsWith('pu');
          if (user.role === 'byggare' && isSelf && isPU) return; // byggare: visa inte egna PU-notiser
          if (isSelf && !isPU) return;
          
          setNotifications(prev => {
            const exists = prev.some(n => n._id === data.notification._id);
            if (!exists) {
              setUnreadCount(prev => prev + 1);
              // Prefetch label för denna notis om den saknas
              const meta = data.notification.metadata || {};
              if (!meta.projectId) {
                fetchProjectLabelForNotification(data.notification).then(label => {
                  if (label) setProjectLabelMap(pm => ({ ...pm, [data.notification._id]: label }));
                });
              }
              return [data.notification, ...prev];
            }
            return prev;
          });
        } else if (data?.type === 'pu_reply' || data?.type === 'pu_comment') {
          // Tysta icke-standard PU-events (hanteras lokalt i PU-vyerna)
          return;
        } else {
          // Lämna övriga icke-standard events tysta för att undvika brus i konsolen
          return;
        }
      });
    }

    return () => {
      if (socket) {
        socket.off('notification');
      }
    };
  }, [socket, user, fetchProjectLabelForNotification]);

  const toggleReadStatus = async (notification, event) => {
    // Förhindra event bubbling så klicket inte stänger dropdown
    if (event) {
      event.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = notification.read ? 'unread' : 'read';
      
      const response = await fetch(`${API_URL}/notifications/${notification._id}/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setNotifications(prev => {
          const updatedNotifications = prev.map(notif => 
            notif._id === notification._id 
              ? { ...notif, read: !notif.read }
              : notif
          );
          
          return updatedNotifications;
        });
        setUnreadCount(prev => notification.read ? prev + 1 : Math.max(0, prev - 1));
        // Ta bort automatisk stängning av dropdown
        // setIsOpen(false);
      } else {
        console.error('Server fel vid status-ändring:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Kunde inte uppdatera notifiering status:', error);
    }
  };

  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kunde inte ta bort notifiering');
      }

      // Uppdatera UI
      setNotifications(prev => {
        const updatedNotifications = prev.filter(n => n._id !== notificationId);
        // Uppdatera unreadCount om den borttagna notifieringen var oläst
        const removedNotification = prev.find(n => n._id === notificationId);
        if (removedNotification && !removedNotification.read) {
          setUnreadCount(current => Math.max(0, current - 1));
        }
        
        return updatedNotifications;
      });
    } catch (error) {
      console.error('Fel vid borttagning av notifiering:', error);
    }
  };

  const handleGoToProject = (notification) => {
    setIsOpen(false);

    // Vart notisen leder ligger i notificationRoutes. Här låg tidigare en
    // switch per roll där 42 av 58 typer föll till samma default.
    const path = byggNotisLank(notification, user);
    if (!path) {
      console.warn('Ingen länk för notis:', notification.type, notification._id);
      return;
    }

    navigate(path, { state: { fromNotification: true } });
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notifications/read-all`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte markera notifieringar som lästa');
      }

      // Uppdatera lokalt state
      setNotifications(prev => {
        const updatedNotifications = prev.map(notif => ({ ...notif, read: true }));
        
        return updatedNotifications;
      });
      setUnreadCount(0);
    } catch (error) {
      console.error('Fel vid markering av notifieringar som lästa:', error);
    }
  };

  /**
   * Ta bort alla lästa notiser. Servern avgör vilka — bara sådana användaren
   * kan ha sett — och svarar med id:na, så listan här tar bort exakt dem.
   */
  const deleteAllRead = async () => {
    const antal = visibleNotifications.filter(n => n.read).length;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Ta bort ${antal} läst${antal === 1 ? '' : 'a'} notis${antal === 1 ? '' : 'er'}? Det går inte att ångra.`)) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notifications/read`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Kunde inte ta bort lästa notiser');

      const borttagna = new Set(data.ids || []);
      setNotifications(prev => prev.filter(n => !borttagna.has(String(n._id))));
    } catch (error) {
      console.error('Fel vid borttagning av lästa notiser:', error);
    }
  };

  // Admin-funktion för att städa notifikationer med ogiltiga referenser
  const runCleanupOrphanedNotifications = async () => {
    try {
      setCleanupLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/notifications/admin/cleanup-orphaned`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kunde inte köra cleanup');
      }

      const result = await response.json();
      // Uppdatera notifikationer efter cleanup
      await fetchNotifications();
      
      // Visa bekräftelse
      alert(result.message);
      
    } catch (error) {
      console.error('❌ Fel vid cleanup:', error);
      alert(`Fel vid cleanup: ${error.message}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  // 🚀 OPTIMERING: Ta bort automatisk polling - bara vid inloggning och manuellt
  // Notifikationer uppdateras via:
  // 1. Vid komponenten laddar (inloggning)
  // 2. Via socket för realtidsuppdateringar
  // 3. Manuellt via uppdateringsknapp

  useEffect(() => {
    // Filtrera bort notifikationer som inte ska visas än + rollbaserad PU‑självfilter
    const filteredNotifications = notifications.filter(notification => {
      if (notification.type === 'timeline_reminder') {
        const reminderDate = new Date(notification.reminderDate);
        const now = new Date();
        if (reminderDate > now) return false;
      }
      // Byggare ska inte se egna PU‑notiser
      const isSelf = String(notification.actionUserId) === String(user?._id);
      const isPU = String(notification.type || '').startsWith('pu');
      if (user?.role === 'byggare' && isSelf && isPU) return false;
      return true;
    });

    setVisibleNotifications(filteredNotifications);
    
    // Uppdatera unreadCount baserat på synliga notifikationer
    const unreadNotifications = filteredNotifications.filter(n => !n.read).length;
    setUnreadCount(unreadNotifications);
  }, [notifications, user?._id, user?.role]);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-leadme-red rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium pl-2">
                Notifieringar
                {unreadCount > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-leadme-pink text-white">
                    {unreadCount} oläst{unreadCount !== 1 ? 'a' : ''}
                  </span>
                )}
              </h3>
              <div className="flex items-center space-x-2">
                {/* Manuell uppdateringsknapp */}
                <button
                  onClick={fetchNotifications}
                  className="p-1 text-gray-600 hover:text-leadme-pink-dark rounded transition-colors"
                  title="Uppdatera notifikationer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                {notifications.length > 0 && unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-leadme-pink-dark hover:text-leadme-gray-dark px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    Markera alla som lästa
                  </button>
                )}
                {visibleNotifications.some(n => n.read) && (
                  <button
                    onClick={deleteAllRead}
                    className="text-xs text-gray-600 hover:text-red-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                    title="Ta bort alla lästa notiser"
                  >
                    Ta bort lästa
                  </button>
                )}
                {user?.role === 'admin' && (
                  <button
                    onClick={runCleanupOrphanedNotifications}
                    disabled={cleanupLoading}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      cleanupLoading
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                    }`}
                    title="Städa notifikationer med ogiltiga referenser"
                  >
                    {cleanupLoading ? 'Städar...' : '🧹 Städa'}
                  </button>
                )}
                {/* Stäng-knapp */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-gray-600 hover:text-gray-800 rounded transition-colors"
                  title="Stäng"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {visibleNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="mb-3">
                  <svg className="w-12 h-12 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">Inga notifieringar just nu</p>
                <p className="text-gray-400 text-xs mt-1">Vi meddelar dig när något händer</p>
              </div>
            ) : (
              visibleNotifications.map(notification => {
                // Konvertera projektdata till rätt format
                const projectData = notification.projectId || {};
                const metadata = notification.metadata || {};
                
                // Samla all information på ett ställe
                const displayData = {
                  title: metadata.title || notification.message,
                  description: metadata.description || notification.notificationMessage || projectData.notificationMessage,
                  projectName: metadata.projectName || projectLabelMap[notification._id] || undefined,
                  customerName: metadata.customerName || (typeof projectData === 'object' ? projectData.customer?.name : undefined),
                  // Rå-id:t duger. Det kan vara ett Project-, Lead- eller
                  // Procurement-id beroende på notistyp, och routingen vet vilket
                  // den behöver. Tidigare krävdes ett populerat Project-objekt här,
                  // vilket tyst tog bort länken för estimat, offert och upphandling.
                  projectId: (typeof projectData === 'object' ? projectData._id : projectData),
                  leadId: metadata.projectId // för fallback label/hämtning
                };

                // Formatera visningen med titel och beskrivning kombinerade
                const displayTitle = displayData.title;
                const titleWithDescription = (displayData.description && displayData.description !== displayData.title);

                return (
                  <div
                    key={notification._id}
                    className={`group p-4 border-b transition-colors duration-200 ${
                      !notification.read ? 'bg-blue-50 border-l-4 border-l-leadme-pink' : 'bg-white'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-start space-x-3">
                          {/* Läst/Oläst indikator */}
                          <div className="flex-shrink-0 mt-1">
                            {!notification.read ? (
                              <div className="w-2.5 h-2.5 bg-leadme-pink rounded-full" title="Oläst"></div>
                            ) : (
                              <div className="w-2.5 h-2.5 bg-gray-300 rounded-full" title="Läst"></div>
                            )}
                          </div>
                          
                          <div className="flex-1 space-y-2">
                            {notification.type === 'timeline_reminder' && titleWithDescription ? (
                              <div className="text-sm">
                                <span className={!notification.read ? 'font-medium' : ''}>{displayData.title}</span>
                                <span> - {displayData.description}</span>
                              </div>
                            ) : (
                              <p className={`text-sm ${!notification.read ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                                {displayTitle}
                              </p>
                            )}
                            {displayData.description && displayData.description !== displayData.title && notification.type !== 'timeline_reminder' && (
                              <p className="text-sm text-gray-700">
                                {displayData.description}
                              </p>
                            )}
                            {metadata.reviewReplyText && (
                              <div className="mt-1 space-y-1">
                                {/* Granskningspunkten som besvarades */}
                                {metadata.reviewQuestionText && (
                                  <p className="text-sm text-gray-600 bg-gray-50 border-l-2 border-gray-300 pl-2 py-1 whitespace-pre-wrap break-words">
                                    <span className="font-medium text-gray-400">Kommentar: </span>
                                    {metadata.reviewQuestionText}
                                  </p>
                                )}
                                <p className="text-sm text-gray-600 bg-gray-50 border-l-2 border-leadme-pink/40 pl-2 py-1 whitespace-pre-wrap break-words">
                                  {metadata.reviewQuestionText && (
                                    <span className="font-medium text-gray-400">Svar: </span>
                                  )}
                                  {metadata.reviewQuestionText ? metadata.reviewReplyText : `"${metadata.reviewReplyText}"`}
                                </p>
                              </div>
                            )}
                            {/* Gatet på user?.role !== 'kund' som låg här gjorde att
                                kunder inte kunde klicka på någon notis alls — hela
                                kund-routingen var död kod. */}
                            {(() => {
                              const fallbackLabel = projectLabelMap[notification._id] || null;
                              const label = displayData.projectName || fallbackLabel || displayData.customerName || '-';
                              // Klickbar om routingen hittar en sökväg. Kravet på ett
                              // populerat Project dödade tidigare länken för allt som
                              // hänger på Lead eller Procurement — estimat, offert,
                              // upphandling och PU.
                              const canNavigate = !!byggNotisLank(notification, user);
                              return (
                                <div className="text-xs text-gray-600">
                                  Projekt: {' '}
                                  {canNavigate ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleGoToProject(notification);
                                      }}
                                      className="text-leadme-pink-dark hover:text-leadme-gray-dark underline"
                                    >
                                      {label}
                                    </button>
                                  ) : (
                                    <span>{label}</span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center mt-3">
                          <p className="text-xs text-gray-500">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                          <div className="flex space-x-2">
                            {/* Läst/Oläst toggle knapp */}
                            <button
                              onClick={(e) => toggleReadStatus(notification, e)}
                              className={`text-xs px-2 py-1 rounded transition-colors duration-200 ${
                                notification.read 
                                  ? 'text-gray-600 hover:text-leadme-pink-dark hover:bg-gray-100' 
                                  : 'text-leadme-pink-dark hover:text-gray-600 hover:bg-blue-100'
                              }`}
                              title={notification.read ? 'Markera som oläst' : 'Markera som läst'}
                            >
                              {notification.read ? (
                                <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {notification.read ? 'Oläst' : 'Läst'}
                            </button>
                            
                            <button
                              onClick={(e) => deleteNotification(notification._id, e)}
                              className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors duration-200"
                              title="Ta bort notifiering"
                            >
                              <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Ta bort
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationIcon;